/**
 * Generic component that has a search field to search for a type of user, returns
 * the matching users along along with a table below to select and filter. Results
 * are paginated
 */
import { NavigateBefore, NavigateNext } from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import {
	Fab,
	Grid,
	List,
	ListItem,
	ListItemText,
	Paper,
	styled,
	TextField,
	Typography,
} from "@mui/material";
import { api, types } from "api";
import { InfoSnackBar } from "components/InfoSnackBar";
import { StyledButton } from "components/StyledButton";
import { NO_MATCHING_USERS } from "messages";
import { useEffect, useState } from "react";
import "./CustomMultiSearch.scss";

const PREFIX = "CustomMultiSearch";

const classes = {
	wrapper: `${PREFIX}-wrapper`,
	flexBox: `${PREFIX}-flexBox`,
	textField: `${PREFIX}-textField`,
	searchBlock: `${PREFIX}-searchBlock`,
};

const Root = styled("form")(({ theme }) => ({
	marginTop: theme.spacing(1),

	[`& .${classes.wrapper}`]: {
		backgroundColor: theme.palette.background.default,
		marginTop: theme.spacing(2),
		marginRight: theme.spacing(2),
		width: "85%",
	},
	[`& .${classes.flexBox}`]: {
		flexWrap: "inherit",
	},

	[`& .${classes.textField}`]: {
		"& .MuiInput-underline:after": {
			borderBottomColor: "transparent",
		},
		"& .MuiOutlinedInput-root": {
			"& fieldset": {
				borderColor: "transparent",
			},
			"&:hover fieldset": {
				borderColor: "transparent",
			},
			"&.Mui-focused fieldset": {
				borderColor: "transparent",
			},
		},
	},

	[`& .${classes.searchBlock}`]: {
		paddingRight: theme.spacing(2),
		paddingBottom: theme.spacing(2),
		marginBottom: theme.spacing(2),
	},
}));

// Max users to return for matches with search term
const MAX_COUNT = 15;

export interface CustomMultiSearchProps {
	role: types.RoleType;
	handler: (user: types.User) => void;
}

export const CustomMultiSearch = ({
	role,
	handler,
}: CustomMultiSearchProps): JSX.Element => {
	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [matchedUsers, setMatchedUsers] = useState<types.User[]>([]);
	const [err, setErr] = useState(false);
	const [startDB, setStartDB] = useState(0);

	useEffect(() => {
		// reset pagination when query changes
		setStartDB(0);
	}, [firstName, lastName]);

	// searches the database, with the index provided or the default is startDB
	const searchDB = (start: number = startDB): void => {
		api
			.getUsers({ role, firstName, lastName, start, count: MAX_COUNT })
			.then((res) => {
				if (res.status !== 200) {
					setMatchedUsers([]);
					setErr(true);
					return;
				}
				if (res.data === null) {
					setMatchedUsers([]);
					return;
				}

				setMatchedUsers(res.data);
				setErr(false);

				if (res.data.length === 0) {
					setStartDB(-1);
				}
			})
			.catch(() => {
				setMatchedUsers([]);
				setErr(true);
			});
	};

	// when search button clicked
	const searchHandler = (event: React.FormEvent): void => {
		event.preventDefault();
		searchDB(0);
	};

	const addSelectedUser = (user: types.User): void => {
		handler(user);
		// reset pagination index as the dropdown closes
		setStartDB(0);
		setMatchedUsers([]);
	};
	// pagination function to show previous page of results
	const viewPreviousPage = (): void => {
		setStartDB(startDB - MAX_COUNT);
	};
	// pagination function to show next page of results
	const viewNextPage = (): void => {
		setStartDB(startDB + MAX_COUNT);
	};

	// searches the db after the next or previous page is requested
	useEffect(() => {
		if (startDB > 0) {
			searchDB();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [startDB]);

	// Dropdown with matched users displayed, with pagination
	const MatchedUsersDropDown = (
		<Paper className={classes.searchBlock}>
			<List>
				{matchedUsers.map((user) => (
					<ListItem
						key={user.firstName + user.lastName}
						dense
						button
						onClick={() => addSelectedUser(user)}
					>
						<ListItemText primary={`${user.firstName} ${user.lastName}`} />
					</ListItem>
				))}
				<Grid container justifyContent="flex-end">
					<Fab
						color="primary"
						aria-label="previous"
						disabled={startDB === 0}
						onClick={viewPreviousPage}
					>
						<NavigateBefore />
					</Fab>
					<Fab
						color="primary"
						aria-label="next"
						disabled={startDB === -1}
						onClick={viewNextPage}
					>
						<NavigateNext />
					</Fab>
				</Grid>
			</List>
		</Paper>
	);

	return (
		<Root onSubmit={searchHandler}>
			<Typography variant="h5">Add students to your custom group</Typography>

			<Grid
				container
				direction="row"
				justifyContent="flex-start"
				alignItems="center"
				className={classes.flexBox}
			>
				<div id="search-wrapper" className={classes.wrapper}>
					<TextField
						className={classes.textField}
						label="First Name"
						variant="outlined"
						color="primary"
						data-testid="first-name"
						value={firstName}
						onChange={(e) => setFirstName(e.target.value)}
						fullWidth
					/>
					<div id="search-divider" />
					<TextField
						className={classes.textField}
						label="Last Name"
						variant="outlined"
						color="primary"
						data-testid="last-name"
						value={lastName}
						onChange={(e) => setLastName(e.target.value)}
						fullWidth
					/>
				</div>
				<StyledButton
					variant="contained"
					color="primary"
					size="large"
					startIcon={<SearchIcon />}
					type="submit"
				>
					Search
				</StyledButton>
			</Grid>
			{matchedUsers.length > 0 && MatchedUsersDropDown}
			<InfoSnackBar
				isOpen={err}
				onClose={() => setErr(false)}
				severity="error"
				message={NO_MATCHING_USERS}
			/>
		</Root>
	);
};
